<script lang="ts">
  /**
   * CharacterCreation — 3-step flow: background, archetype, review.
   * LLM generates backgrounds and assigns GURPS stats.
   */
  import "../../styles/wizard.css";
  import { CharacterAgent } from "$lib/api/agents";
  import type { BackgroundOption, CharacterAssignment } from "$lib/api/agents";
  import { updateCharacter } from "$lib/api/invoke";
  import AsciiAnimation from "./AsciiAnimation.svelte";

  interface Props {
    worldSetting: string;
    onComplete: () => void;
    onBack: () => void;
  }

  let { worldSetting, onComplete, onBack }: Props = $props();

  type Step = "background" | "archetype" | "review";
  let step = $state<Step>("background");

  /* Background step */
  let backgrounds = $state<BackgroundOption[]>([]);
  let backgroundLoading = $state(true);
  let backgroundError = $state("");
  let selectedBackground = $state<BackgroundOption | null>(null);
  let customBackground = $state("");
  let showCustom = $state(false);

  /* Archetype step */
  let archetypeText = $state("");

  /* Review step */
  let character = $state<CharacterAssignment | null>(null);
  let reviewLoading = $state(false);
  let reviewError = $state("");

  let agent: CharacterAgent | null = null;

  /** Initialize agent. Router handles provider selection per-call. */
  function initAgent(): CharacterAgent {
    if (!agent) {
      agent = new CharacterAgent({ provider: "gemini", apiKey: "" });
    }
    return agent;
  }

  // Load backgrounds on mount
  $effect(() => {
    void worldSetting; // dependency
    loadBackgrounds();
  });

  async function loadBackgrounds(): Promise<void> {
    backgroundLoading = true;
    backgroundError = "";
    try {
      const a = initAgent();
      backgrounds = await a.generateBackgrounds(worldSetting);
    } catch (e) {
      backgroundError = `Failed to generate backgrounds: ${String(e)}`;
    } finally {
      backgroundLoading = false;
    }
  }

  function selectBg(bg: BackgroundOption): void {
    selectedBackground = bg;
    showCustom = false;
    step = "archetype";
  }

  function useCustomBackground(): void {
    if (!customBackground.trim()) return;
    selectedBackground = { name: "Custom", description: customBackground.trim() };
    step = "archetype";
  }

  async function generateCharacter(): Promise<void> {
    if (!selectedBackground) return;
    reviewLoading = true;
    reviewError = "";
    step = "review";
    try {
      const a = initAgent();
      character = await a.generateCharacter(
        worldSetting,
        selectedBackground.description,
        archetypeText || "a determined adventurer",
      );
    } catch (e) {
      reviewError = `Failed to generate character: ${String(e)}`;
    } finally {
      reviewLoading = false;
    }
  }

  async function acceptCharacter(): Promise<void> {
    if (!character) return;
    try {
      await updateCharacter({
        name: character.name,
        st: character.st,
        dx: character.dx,
        iq: character.iq,
        ht: character.ht,
        skills: character.skills,
        background: selectedBackground?.description ?? "",
        archetype: archetypeText,
        rationale: character.rationale,
      });
      onComplete();
    } catch (e) {
      reviewError = `Failed to save character: ${String(e)}`;
    }
  }

  function handleBack(): void {
    if (step === "background") onBack();
    else if (step === "archetype") step = "background";
    else if (step === "review") step = "archetype";
  }

  /** Render an attribute bar: filled/empty segments out of 20. */
  function attrBar(value: number): string {
    const filled = Math.min(value, 20);
    return "[" + "#".repeat(filled) + "-".repeat(20 - filled) + "] " + value;
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === "Escape") {
      e.preventDefault();
      handleBack();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="cc-root">
  <AsciiAnimation animationLevel="subtle" glyphPercent={10} glyphSpeedMs={300} />
  <div class="wizard">
    {#if step === "background"}
      <p class="wizard__heading">Choose a Background</p>

      {#if backgroundLoading}
        <p class="cc-loading">Conjuring backgrounds...</p>
      {:else if backgroundError}
        <p class="wizard__error">{backgroundError}</p>
        <button class="wizard__btn" onclick={loadBackgrounds}>Retry</button>
      {:else}
        <div class="cc-backgrounds">
          {#each backgrounds as bg}
            <button class="cc-bg-card" onclick={() => selectBg(bg)}>
              <span class="cc-bg-card__name">{bg.name}</span>
              <span class="cc-bg-card__desc">{bg.description}</span>
            </button>
          {/each}
        </div>

        <button
          class="wizard__btn wizard__btn--dim"
          onclick={() => {
            showCustom = !showCustom;
          }}
        >
          {showCustom ? "Hide" : "Write your own"}
        </button>

        {#if showCustom}
          <label class="wizard__label">
            Your background:
            <textarea
              class="wizard__textarea"
              rows={2}
              maxlength={256}
              placeholder="Describe who you were..."
              bind:value={customBackground}
            ></textarea>
          </label>
          <button
            class="wizard__btn"
            disabled={!customBackground.trim()}
            onclick={useCustomBackground}
          >
            Use This Background
          </button>
        {/if}
      {/if}

      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
      </div>
    {:else if step === "archetype"}
      <p class="wizard__heading">Describe Your Character</p>
      {#if selectedBackground}
        <div class="cc-selected-bg">
          <strong>{selectedBackground.name}</strong>: {selectedBackground.description}
        </div>
      {/if}
      <label class="wizard__label">
        What kind of character are you?
        <textarea
          class="wizard__textarea"
          rows={3}
          maxlength={256}
          placeholder="A grizzled warrior who relies on cunning over brute force..."
          bind:value={archetypeText}
        ></textarea>
        <span class="cc-charcount">{archetypeText.length}/256</span>
      </label>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
        <button class="wizard__btn" onclick={generateCharacter}>Roll My Fate</button>
      </div>
    {:else if step === "review"}
      <p class="wizard__heading">Your Character</p>

      {#if reviewLoading}
        <p class="cc-loading">Rolling your fate...</p>
      {:else if reviewError}
        <p class="wizard__error">{reviewError}</p>
      {:else if character}
        <div class="cc-sheet">
          <p class="cc-sheet__name">{character.name}</p>

          <div class="cc-sheet__attrs">
            <div class="cc-attr">
              <span class="cc-attr__label">ST</span>
              <span class="cc-attr__bar">{attrBar(character.st)}</span>
            </div>
            <div class="cc-attr">
              <span class="cc-attr__label">DX</span>
              <span class="cc-attr__bar">{attrBar(character.dx)}</span>
            </div>
            <div class="cc-attr">
              <span class="cc-attr__label">IQ</span>
              <span class="cc-attr__bar">{attrBar(character.iq)}</span>
            </div>
            <div class="cc-attr">
              <span class="cc-attr__label">HT</span>
              <span class="cc-attr__bar">{attrBar(character.ht)}</span>
            </div>
          </div>

          {#if Object.keys(character.skills).length > 0}
            <div class="cc-sheet__skills">
              <p class="cc-sheet__section">Skills:</p>
              {#each Object.entries(character.skills) as [skill, points]}
                <span class="cc-skill">{skill} ({points}pt{points > 1 ? "s" : ""})</span>
              {/each}
            </div>
          {/if}

          <p class="cc-sheet__rationale">{character.rationale}</p>
        </div>
      {/if}

      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
        {#if character && !reviewLoading}
          <button class="wizard__btn wizard__btn--dim" onclick={generateCharacter}>Reroll</button>
          <button class="wizard__btn" onclick={acceptCharacter}>Accept</button>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .cc-root {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* Center content for interactive selection screens */
  .cc-root :global(.wizard) {
    align-items: center;
    text-align: center;
  }

  .cc-root :global(.wizard__label) {
    text-align: left;
  }

  .cc-root :global(.wizard__actions) {
    justify-content: center;
  }

  .cc-loading {
    color: var(--fg-dim);
    font-style: italic;
    text-align: center;
    padding: 2rem 0;
    font-family: var(--font-mono);
  }

  .cc-backgrounds {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    width: 40ch;
    margin: 0.5rem 0;
  }

  .cc-bg-card {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.4rem 0.75rem;
    background: none;
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    cursor: pointer;
    text-align: left;
  }

  .cc-bg-card:hover,
  .cc-bg-card:focus {
    border-color: var(--fg-bright);
    color: var(--fg-bright);
  }

  .cc-bg-card__name {
    font-weight: bold;
    font-size: 0.9rem;
  }

  .cc-bg-card__desc {
    color: var(--fg-dim);
    font-size: 0.8rem;
  }

  .cc-selected-bg {
    color: var(--fg-dim);
    font-size: 0.8rem;
    max-width: 40ch;
    margin: 0 0 0.5rem;
    padding: 0.25rem 0.5rem;
    border-left: 2px solid var(--fg-dim);
    font-family: var(--font-mono);
    text-align: left;
  }

  .cc-charcount {
    color: var(--fg-dim);
    font-size: 0.7rem;
    text-align: right;
    display: block;
  }

  .cc-sheet {
    max-width: 36ch;
    margin: 0;
    font-family: var(--font-mono);
    text-align: left;
  }

  .cc-sheet__name {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--fg-bright);
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .cc-sheet__attrs {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin-bottom: 0.5rem;
  }

  .cc-attr {
    display: flex;
    gap: 0.5rem;
    font-size: 0.85rem;
  }

  .cc-attr__label {
    width: 2.5ch;
    color: var(--fg-bright);
    font-weight: bold;
  }

  .cc-attr__bar {
    color: var(--fg-dim);
    font-size: 0.8rem;
  }

  .cc-sheet__skills {
    margin-bottom: 0.5rem;
  }

  .cc-sheet__section {
    color: var(--fg-bright);
    font-size: 0.85rem;
    margin-bottom: 0.15rem;
  }

  .cc-skill {
    display: inline-block;
    color: var(--fg);
    font-size: 0.8rem;
    margin-right: 0.75rem;
  }

  .cc-sheet__rationale {
    color: var(--fg-dim);
    font-size: 0.8rem;
    font-style: italic;
    margin-top: 0.5rem;
  }
</style>
